import { redirectToLogin } from './client'

// Shape of the `payload` object carried by a telemetry SSE event. Every field
// is optional because the backend emits a different subset per event type
// (see TelemetryService._handle_event) — consumers switch on `event_type` and
// read only the fields that event carries. Declared as a `type` (not an
// `interface`) so it stays assignable to `Record<string, unknown>`, which is
// what copilotStore.handleEvent accepts.
export type CallEventPayload = {
  direction?: string
  mode?: string
  from_number?: string
  to_number?: string
  started_at?: number
  ended_by?: string
  ended_reason?: string
  recording_url?: string
  summary?: string
  cost_usd?: number
  cost_breakdown?: Record<string, unknown> | null
  listen_url?: string
  control_url?: string
  phone_call_provider_id?: string
}

// A single decoded SSE frame. The backend sometimes nests the event body under
// `payload` and sometimes flattens it onto the envelope, hence `data.payload ||
// data` at the call sites — both branches yield a CallEventPayload.
export type CallEventData = CallEventPayload & {
  event_type?: string
  call_sid?: string
  timestamp?: number
  payload?: CallEventPayload
}

type SSEEventHandler = (eventType: string, data: CallEventData) => void

export type SSEConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting'
type SSEStateChangeHandler = (state: SSEConnectionState) => void

export class SSEManager {
  private eventSource: EventSource | null = null
  private handlers: Set<SSEEventHandler> = new Set()
  private stateHandlers: Set<SSEStateChangeHandler> = new Set()
  private url: string
  private reconnectDelay = 1000
  private maxReconnectDelay = 30000
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private _connectionState: SSEConnectionState = 'disconnected'

  constructor(url: string) {
    this.url = url
  }

  private setConnectionState(state: SSEConnectionState): void {
    this._connectionState = state
    this.stateHandlers.forEach(handler => handler(state))
  }

  get connectionState(): SSEConnectionState {
    return this._connectionState
  }

  connect(): void {
    if (this.eventSource) return

    this.setConnectionState('connecting')
    this.eventSource = new EventSource(this.url)
    
    this.eventSource.onopen = () => {
      this.reconnectDelay = 1000
      this.setConnectionState('connected')
    }

    this.eventSource.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data)
        // The backend now sends batched arrays of events.
        // For backward compat, also handle a single object.
        const items: CallEventData[] = Array.isArray(parsed) ? parsed : [parsed]
        for (const data of items) {
          const eventType = data.event_type || 'unknown'
          this.handlers.forEach(handler => handler(eventType, data))
        }
      } catch (err) {
        console.error('SSE parse error:', err)
      }
    }

    this.eventSource.onerror = () => {
      this.closeEventSource()
      void this.probeAuthThenReconnect()
    }
  }

  // EventSource doesn't expose HTTP status codes, so after a connection drop
  // we probe a gated endpoint to distinguish auth loss from transient errors.
  // On 401 we send the browser through the OAuth redirect; anything else
  // falls back to the normal exponential reconnect.
  private async probeAuthThenReconnect(): Promise<void> {
    try {
      const res = await fetch('/telemetry/stats', {
        headers: { Accept: 'application/json' },
      })
      if (res.status === 401) {
        redirectToLogin()
        return
      }
    } catch {
      // network error — fall through to reconnect
    }
    this.scheduleReconnect()
  }

  private closeEventSource(): void {
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }
  }

  disconnect(): void {
    this.closeEventSource()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.setConnectionState('disconnected')
  }

  private scheduleReconnect(): void {
    this.setConnectionState('reconnecting')
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, this.reconnectDelay)
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay)
  }

  subscribe(handler: SSEEventHandler): () => void {
    this.handlers.add(handler)
    return () => {
      this.handlers.delete(handler)
    }
  }

  onStateChange(handler: SSEStateChangeHandler): () => void {
    this.stateHandlers.add(handler)
    return () => {
      this.stateHandlers.delete(handler)
    }
  }

  get isConnected(): boolean {
    return this._connectionState === 'connected'
  }
}
